import { useState } from 'react';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunDetailsGate from 'app/components/Reconciliations/ReconciliationRunDetailsGate';
import ReconciliationPagedTable from 'app/components/Reconciliations/ReconciliationPagedTable';

import { defaultPageSize } from 'app/constants/table';

import useFetchUnverifiableAggregates from 'app/hooks/useFetchUnverifiableAggregates';

import getUnverifiableAggregateColumns from 'app/utils/table/unverifiableAggregates';

interface ReconciliationNotVerifiableTabProps {
  reconciliationId: string;
  companyId?: string;
  isRunning: boolean;
  detailsAvailable: boolean;
}

/** Lists the aggregates a reconciliation run could not verify, with the reasons for each. */
function ReconciliationNotVerifiableTab({
  reconciliationId,
  companyId = undefined,
  isRunning,
  detailsAvailable,
}: ReconciliationNotVerifiableTabProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const { data, isLoading, isError } = useFetchUnverifiableAggregates(
    {
      id: reconciliationId,
      companyId,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    !isRunning && detailsAvailable
  );

  return (
    <ReconciliationRunDetailsGate
      isRunning={isRunning}
      detailsAvailable={detailsAvailable}
      pendingText="What it could not verify"
    >
      <ReconciliationPagedTable
        page={data}
        isLoading={isLoading}
        isError={isError}
        columns={getUnverifiableAggregateColumns(!companyId)}
        pagination={pagination}
        onPaginationChange={setPagination}
        getRowId={(aggregate) => aggregate.periodAggregateId}
        emptyText="Every aggregate in this run could be verified"
        errorText="Sorry, we failed to fetch the aggregates that could not be verified. Please try again."
        testId="reconciliation-not-verifiable"
      />
    </ReconciliationRunDetailsGate>
  );
}

export default ReconciliationNotVerifiableTab;
